import React, { useState, useEffect, useCallback, useRef } from "react";
import Modal from '@mui/material/Modal';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useMediaQuery,
  Card,
  CardMedia,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  TableHead,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Pagination,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText
} from "@mui/material";
import {
  Refresh,
  GridView,
  List as ListIcon,
  Receipt,
  CalendarToday,
  Download,
  Close,
  AttachFile as AttachFileIconMUI
} from "@mui/icons-material";
import Notes from "./header";
import FooterManual from "./footerManual";
import { Link } from 'react-router-dom';
import { styled } from "@mui/material/styles";

// Create axios instance with default configs
const api = axios.create({
  baseURL: "https://car.amar.co.jp/api",
  timeout: 10000,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const StatusChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  fontWeight: 'bold',
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
  overflow: "hidden",
  marginBottom: "16px",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    width: `calc(100% + ${theme.spacing(2)})`,
    "& .MuiTable-root": { minWidth: "650px" },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  [theme.breakpoints.down("sm")]: {
    "& > .MuiTableCell-root": { padding: theme.spacing(1) },
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    fontSize: "0.75rem",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiCardMedia-root": { height: "120px" },
    "& .MuiCardContent-root": { padding: "20px 8px" },
    "& .MuiButton-root": {
      fontSize: "0.7rem",
      padding: "4px 8px",
      minWidth: "unset",
    },
    "& .MuiTypography-h6": { fontSize: "0.9rem" },
    "& .MuiTypography-body2": { fontSize: "0.75rem" },
    "& .MuiChip-root": {
      height: "20px",
      "& .MuiChip-label": { fontSize: "0.65rem" },
    },
  },
}));

const SpinningLogo = styled("img")(({ theme }) => ({
  width: "60px",
  height: "60px",
  animation: "spin 1.5s linear infinite",
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

// Country codes list (same as Inquiry History)
const COUNTRY_CODES = [
  { code: '+213', flag: '🇩🇿', country: 'Algeria', countryId: 26 },
  { code: '+1-684', flag: '🇦🇸', country: 'American Samoa', countryId: 67 },
  { code: '+244', flag: '🇦🇴', country: 'Angola', countryId: 28 },
  { code: '+1-268', flag: '🇦🇬', country: 'Antigua', countryId: 82 },
  { code: '+61', flag: '🇦🇺', country: 'Australia', countryId: 50 },
  { code: '+1-242', flag: '🇧🇸', country: 'Bahamas', countryId: 83 },
  { code: '+973', flag: '🇧🇭', country: 'Bahrain', countryId: 59 },
  { code: '+880', flag: '🇧🇩', country: 'Bangladesh', countryId: 52 },
  { code: '+1-246', flag: '🇧🇧', country: 'Barbados', countryId: 84 },
  { code: '+32', flag: '🇧🇪', country: 'Belgium', countryId: 105 },
  { code: '+229', flag: '🇧🇯', country: 'Benin', countryId: 29 },
  { code: '+1-441', flag: '🇧🇲', country: 'Bermuda', countryId: 85 },
  { code: '+267', flag: '🇧🇼', country: 'Botswana', countryId: 17 },
  { code: '+673', flag: '🇧🇳', country: 'Brunei', countryId: 45 },
  { code: '+226', flag: '🇧🇫', country: 'Burkina Faso', countryId: 34 },
  { code: '+257', flag: '🇧🇮', country: 'Burundi', countryId: 9 },
  { code: '+237', flag: '🇨🇲', country: 'Cameroon', countryId: 30 },
  { code: '+1', flag: '🇨🇦', country: 'Canada', countryId: 104 },
  { code: '+1-345', flag: '🇰🇾', country: 'Cayman Island', countryId: 86 },
  { code: '+56', flag: '🇨🇱', country: 'Chile', countryId: 103 },
  { code: '+57', flag: '🇨🇴', country: 'Colombia', countryId: 87 },
  { code: '+225', flag: '🇨🇮', country: 'Cote D\'Ivoire', countryId: 31 },
  { code: '+357', flag: '🇨🇾', country: 'Cyprus', countryId: 115 },
  { code: '+243', flag: '🇨🇩', country: 'DR Congo', countryId: 12 },
  { code: '+1-767', flag: '🇩🇲', country: 'Dominica', countryId: 88 },
  { code: '+20', flag: '🇪🇬', country: 'Egypt', countryId: 24 },
  { code: '+291', flag: '🇪🇷', country: 'Eritrea', countryId: 3 },
  { code: '+251', flag: '🇪🇹', country: 'Ethiopia', countryId: 4 },
  { code: '+679', flag: '🇫🇯', country: 'Fiji', countryId: 68 },
  { code: '+358', flag: '🇫🇮', country: 'Finland', countryId: 106 },
  { code: '+33', flag: '🇫🇷', country: 'France', countryId: 107 },
  { code: '+241', flag: '🇬🇦', country: 'Gabon', countryId: 32 },
  { code: '+220', flag: '🇬🇲', country: 'Gambia', countryId: 33 },
  { code: '+49', flag: '🇩🇪', country: 'Germany', countryId: 108 },
  { code: '+233', flag: '🇬🇭', country: 'Ghana', countryId: 35 },
  { code: '+30', flag: '🇬🇷', country: 'Greece', countryId: 109 },
  { code: '+1-473', flag: '🇬🇩', country: 'Grenada', countryId: 89 },
  { code: '+590', flag: '🇬🇵', country: 'Guadeloupe', countryId: 90 },
  { code: '+224', flag: '🇬🇳', country: 'Guinea', countryId: 36 },
  { code: '+592', flag: '🇬🇾', country: 'Guyana', countryId: 91 },
  { code: '+509', flag: '🇭🇹', country: 'Haiti', countryId: 92 },
  { code: '+852', flag: '🇭🇰', country: 'Hongkong', countryId: 43 },
  { code: '+91', flag: '🇮🇳', country: 'India', countryId: 56 },
  { code: '+62', flag: '🇮🇩', country: 'Indonesia', countryId: 44 },
  { code: '+353', flag: '🇮🇪', country: 'Ireland', countryId: 110 },
  { code: '+1-876', flag: '🇯🇲', country: 'Jamaica', countryId: 93 },
  { code: '+254', flag: '🇰🇪', country: 'Kenya', countryId: 6 },
  { code: '+686', flag: '🇰🇮', country: 'Kiribati', countryId: 69 },
  { code: '+965', flag: '🇰🇼', country: 'Kuwait', countryId: 60 },
  { code: '+961', flag: '🇱🇧', country: 'Lebanon', countryId: 66 },
  { code: '+231', flag: '🇱🇷', country: 'Liberia', countryId: 37 },
  { code: '+218', flag: '🇱🇾', country: 'Libya', countryId: 25 },
  { code: '+261', flag: '🇲🇬', country: 'Madagascar', countryId: 21 },
  { code: '+265', flag: '🇲🇼', country: 'Malawi', countryId: 14 },
  { code: '+60', flag: '🇲🇾', country: 'Malaysia', countryId: 46 },
  { code: '+960', flag: '🇲🇻', country: 'Maldives', countryId: 55 },
  { code: '+356', flag: '🇲🇹', country: 'Malta', countryId: 117 },
  { code: '+692', flag: '🇲🇭', country: 'Marshall Islands', countryId: 70 },
  { code: '+596', flag: '🇲🇶', country: 'Martinique', countryId: 94 },
  { code: '+222', flag: '🇲🇷', country: 'Mauritania', countryId: 38 },
  { code: '+230', flag: '🇲🇺', country: 'Mauritius', countryId: 23 },
  { code: '+691', flag: '🇫🇲', country: 'Micronesia', countryId: 71 },
  { code: '+212', flag: '🇲🇦', country: 'Morocco', countryId: 27 },
  { code: '+258', flag: '🇲🇿', country: 'Mozambique', countryId: 15 },
  { code: '+95', flag: '🇲🇲', country: 'Myanmar', countryId: 53 },
  { code: '+264', flag: '🇳🇦', country: 'Namibia', countryId: 19 },
  { code: '+977', flag: '🇳🇵', country: 'Nepal', countryId: 54 },
  { code: '+31', flag: '🇳🇱', country: 'Netherland', countryId: 116 },
  { code: '+599', flag: '🇧🇶', country: 'Netherlands Antilles', countryId: 95 },
  { code: '+687', flag: '🇳🇨', country: 'New Caledonia', countryId: 72 },
  { code: '+64', flag: '🇳🇿', country: 'New Zealand', countryId: 51 },
  { code: '+234', flag: '🇳🇬', country: 'Nigeria', countryId: 39 },
  { code: '+47', flag: '🇳🇴', country: 'Norway', countryId: 111 },
  { code: '+968', flag: '🇴🇲', country: 'Oman', countryId: 61 },
  { code: '+92', flag: '🇵🇰', country: 'Pakistan', countryId: 57 },
  { code: '+680', flag: '🇵🇼', country: 'Palau', countryId: 73 },
  { code: '+507', flag: '🇵🇦', country: 'Panama', countryId: 96 },
  { code: '+675', flag: '🇵🇬', country: 'Papua New Guinea', countryId: 74 },
  { code: '+974', flag: '🇶🇦', country: 'Qatar', countryId: 62 },
  { code: '+262', flag: '🇷🇪', country: 'Reunion', countryId: 22 },
  { code: '+7', flag: '🇷🇺', country: 'Russia', countryId: 49 },
  { code: '+250', flag: '🇷🇼', country: 'Rwanda', countryId: 8 },
  { code: '+1-869', flag: '🇰🇳', country: 'ST. Kitts', countryId: 97 },
  { code: '+1-758', flag: '🇱🇨', country: 'ST. Lucia', countryId: 98 },
  { code: '+1-784', flag: '🇻🇨', country: 'ST. Vincent', countryId: 99 },
  { code: '+685', flag: '🇼🇸', country: 'Samoa', countryId: 75 },
  { code: '+966', flag: '🇸🇦', country: 'Saudi Arabia', countryId: 63 },
  { code: '+221', flag: '🇸🇳', country: 'Senegal', countryId: 40 },
  { code: '+232', flag: '🇸🇱', country: 'Sierra Leone', countryId: 41 },
  { code: '+65', flag: '🇸🇬', country: 'Singapore', countryId: 47 },
  { code: '+677', flag: '🇸🇧', country: 'Solomon Islands', countryId: 76 },
  { code: '+252', flag: '🇸🇴', country: 'Somalia', countryId: 5 },
  { code: '+27', flag: '🇿🇦', country: 'South Africa', countryId: 18 },
  { code: '+211', flag: '🇸🇸', country: 'South Sudan', countryId: 2 },
  { code: '+94', flag: '🇱🇰', country: 'Sri Lanka', countryId: 58 },
  { code: '+249', flag: '🇸🇩', country: 'Sudan', countryId: 1 },
  { code: '+597', flag: '🇸🇷', country: 'Suriname', countryId: 101 },
  { code: '+268', flag: '🇸🇿', country: 'Swaziland', countryId: 20 },
  { code: '+46', flag: '🇸🇪', country: 'Sweden', countryId: 112 },
  { code: '+689', flag: '🇵🇫', country: 'Tahiti', countryId: 77 },
  { code: '+255', flag: '🇹🇿', country: 'Tanzania', countryId: 11 },
  { code: '+66', flag: '🇹🇭', country: 'Thailand', countryId: 48 },
  { code: '+228', flag: '🇹🇬', country: 'Togo', countryId: 42 },
  { code: '+676', flag: '🇹🇴', country: 'Tonga', countryId: 78 },
  { code: '+1-868', flag: '🇹🇹', country: 'Trinidad & Tobago', countryId: 100 },
  { code: '+90', flag: '🇹🇷', country: 'Turkey', countryId: 113 },
  { code: '+688', flag: '🇹🇻', country: 'Tuvalu', countryId: 80 },
  { code: '+971', flag: '🇦🇪', country: 'U.A.E', countryId: 64 },
  { code: '+256', flag: '🇺🇬', country: 'Uganda', countryId: 7 },
  { code: '+44', flag: '🇬🇧', country: 'United Kingdom', countryId: 114 },
  { code: '+1', flag: '🇺🇸', country: 'United States', countryId: 79 },
  { code: '+678', flag: '🇻🇺', country: 'Vanuatu', countryId: 81 },
  { code: '+58', flag: '🇻🇪', country: 'Venezuela', countryId: 102 },
  { code: '+967', flag: '🇾🇪', country: 'Yemen', countryId: 65 },
  { code: '+260', flag: '🇿🇲', country: 'Zambia', countryId: 13 },
  { code: '+255', flag: '🇹🇿', country: 'Zanzibar', countryId: 10 },
  { code: '+263', flag: '🇿🇼', country: 'Zimbabwe', countryId: 16 },
  { code: '+81', flag: '🇯🇵', country: 'Japan', countryId: 121 }

];
// Sort alphabetically by country name for better UX
COUNTRY_CODES.sort((x, y) => x.country.localeCompare(y.country));

// ==================== COUNTRY CODE SELECT (same as MyProfile) ====================
// Get ISO code (IN, AU...) from flag emoji so user can search by "IN", "AU" etc.
const getIsoFromFlag = (flag = '') => {
  const chars = Array.from(flag);
  if (chars.length !== 2) return '';
  return chars
    .map((ch) => {
      const cp = ch.codePointAt(0);
      return cp >= 0x1F1E6 && cp <= 0x1F1FF ? String.fromCharCode(cp - 0x1F1E6 + 65) : '';
    })
    .join('');
};

// selectedId: countryId se exact country (US/Canada dono +1) - parent track karta hai
const CountryCodeSelect = ({ value, selectedId, onChange, options = [], disabled = false, error = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const filtered = options.filter(item =>
        item.country.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term) ||
        getIsoFromFlag(item.flag).toLowerCase() === term
      );
      // Priority: exact ISO match (IN -> India) > name startsWith > contains
      const isoMatch = [];
      const startsWith = [];
      const containsOnly = [];
      filtered.forEach(item => {
        if (getIsoFromFlag(item.flag).toLowerCase() === term) {
          isoMatch.push(item);
        } else if (item.country.toLowerCase().startsWith(term)) {
          startsWith.push(item);
        } else {
          containsOnly.push(item);
        }
      });
      setFilteredOptions([...isoMatch, ...startsWith, ...containsOnly]);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (item) => {
    onChange(item.code, item);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearchTerm('');
  };

  const selectedItem =
    (selectedId && options.find(item => item.countryId === selectedId)) ||
    options.find(item => item.code === value);

  const isSelected = (item) => selectedItem && selectedItem.country === item.country;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem ? `${getIsoFromFlag(selectedItem.flag)} ${selectedItem.code}`.trim() : '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder="Search country code..."
          readOnly={!open}
          disabled={disabled}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '16.5px 56px 16.5px 14px',
            border: error ? '2px solid #d32f2f' : '2px solid rgba(229, 62, 62, 0.3)',
            borderRadius: '12px',
            fontSize: '16px',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : (open ? 'text' : 'pointer'),
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            transition: 'all 0.2s',
            height: '56px',
            ...(open && {
              borderColor: '#e53e3e',
              boxShadow: '0 0 0 3px rgba(229, 62, 62, 0.1)'
            })
          }}
        />

        {/* Clear button */}
        {value && !open && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#eb2329';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
            }}
            type="button"
          >
            <svg
              style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Dropdown/search icon */}
        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#eb2329' : '#9ca3af',
            pointerEvents: 'none'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          width: '300px',
          maxHeight: '300px',
          overflow: 'auto',
          zIndex: 1400,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((item, index) => (
              <div
                key={`${item.code}-${item.country}`}
                onClick={() => handleSelect(item)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  backgroundColor: isSelected(item) ? 'rgba(235, 35, 41, 0.12)' : 'white',
                  color: isSelected(item) ? '#eb2329' : '#374151',
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected(item) ? 'rgba(235, 35, 41, 0.2)' : 'rgba(235, 35, 41, 0.08)';
                  e.currentTarget.style.paddingLeft = '20px';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected(item) ? 'rgba(235, 35, 41, 0.12)' : 'white';
                  e.currentTarget.style.paddingLeft = '16px';
                }}
              >
                {/* ISO code capital me (Admin/My Profile jaisa) */}
                <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '28px', color: '#111827' }}>{getIsoFromFlag(item.flag)}</span>
                <span>{item.code}</span>
                <span style={{ marginLeft: 'auto', color: isSelected(item) ? '#eb2329' : '#6b7280', fontSize: '13px', textAlign: 'right' }}>
                  {item.country}
                  {isSelected(item) && ' (Selected)'}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Raise Ticket Dialog Component
const RaiseTicketDialog = ({ open, onClose, orderId, orderRefId, carName, onSuccess }) => {
  const [formData, setFormData] = useState({
    countryCode: '+91',
    alternativeMobileNumber: '',
    orderId: orderId || '',
    orderRefId: orderRefId || '',
    ticketCategory: '',
    subject: '',
    description: '',
    ticketPriority: '',
    attachments: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Field-level errors - saare required fields ek saath highlight honge
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);
  // User's registered country code (from profile) - fallback India
  const [defaultCountryCode, setDefaultCountryCode] = useState('+91');
  // Profile wali exact country ka id (US vs Canada)
  const [defaultCountryId, setDefaultCountryId] = useState(null);

  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        orderId: orderId || '',
        orderRefId: orderRefId || '',
        // Pre-fill subject with car name (same as Inquiry History)
        subject: `Order about ${carName || 'Car'}`,
        ticketCategory: '',
        ticketPriority: '',
        description: ''
      }));
      setError('');
      setSuccess('');
    }
    if (open) setFieldErrors({});
  }, [open, orderId, orderRefId, carName]);

  // Fetch user's registered country code from profile when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchUserCountryCode = async () => {
      try {
        const response = await api.get('/authorization/user/get/user/details', {
          headers: { 'Accept-Language': 'en' }
        });
        if (cancelled) return;
        const userDetails = response?.data?.userDetails || {};

        // Mobile number: strip country code if API sends '+61-9898...' (same as MyProfile)
        let mobile = String(userDetails.userMobileNumber || '');
        if (mobile.startsWith('+')) {
          const match = mobile.match(/\+\d+[-\s]*(\d+)/);
          mobile = match && match[1] ? match[1] : '';
        }
        mobile = mobile.replace(/\D/g, '');

        // API may return '61' or '+61' - normalize to '+61'
        const apiCode = userDetails.countryCode;
        const displayCode = apiCode
          ? (String(apiCode).startsWith('+') ? String(apiCode) : `+${apiCode}`)
          : '';

        // Only apply code if it exists in our dropdown list
        // Pehle profile ke countryId se exact country, na mile to dial code se
        const profileCountry =
          (userDetails.countryId && COUNTRY_CODES.find(c => c.countryId === userDetails.countryId)) ||
          (displayCode && COUNTRY_CODES.find(c => c.code === displayCode));
        if (profileCountry) {
          setDefaultCountryCode(profileCountry.code);
          setDefaultCountryId(profileCountry.countryId || null);
          setFormData(prev => ({ ...prev, countryCode: profileCountry.code, countryCodeId: profileCountry.countryId || null, alternativeMobileNumber: mobile }));
        } else if (mobile) {
          setFormData(prev => ({ ...prev, alternativeMobileNumber: mobile }));
        }
      } catch (err) {
        console.error('Failed to fetch user country code:', err);
      }
    };

    fetchUserCountryCode();
    return () => { cancelled = true; };
  }, [open]);

  const handleCountryCodeChange = (code, item) => {
    setFormData(prev => {
      const newId = item?.countryId || null;
      // Same country = id same (US/Canada dono +1, isliye code se nahi)
      const sameCountry = !!item && (prev.countryCodeId ? newId === prev.countryCodeId : code === prev.countryCode);
      return {
        ...prev,
        countryCode: code,
        countryCodeId: newId,
        // Country badla to phone reset - user naya number daalega
        alternativeMobileNumber: sameCountry ? prev.alternativeMobileNumber : ''
      };
    });
    setFieldErrors(prev => ({ ...prev, countryCode: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // User ne field bhara to uska error hatao
    // (number khali kiya to country code ka error bhi hatao)
    setFieldErrors(prev => {
      const next = { ...prev, [name]: '' };
      if (name === 'alternativeMobileNumber' && !value.trim()) next.countryCode = '';
      return next;
    });
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      attachments: e.target.files[0]
    }));
  };

  const removeAttachment = () => {
    setFormData(prev => ({
      ...prev,
      attachments: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    setError('');
    const errors = {};

    if (!formData.ticketCategory) errors.ticketCategory = 'Ticket category is required';
    if (!formData.subject.trim()) errors.subject = 'Subject is required';
    if (!formData.ticketPriority) errors.ticketPriority = 'Priority is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (formData.alternativeMobileNumber.trim() && !formData.countryCode) {
      errors.countryCode = 'Please select a country code';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Build query params matching the API spec
      const params = new URLSearchParams({
        calculationOrderHistoryId: formData.orderId,
        ticketSubject: formData.subject,
        ticketDescription: formData.description,
        ticketCategory: formData.ticketCategory,
        ticketPriority: formData.ticketPriority,
      });

      // Only include alternativeMobileNumber if provided
      if (formData.alternativeMobileNumber.trim()) {
        const fullPhone = `${formData.countryCode}-${formData.alternativeMobileNumber.trim()}`;
        params.append('alternativeMobileNumber', fullPhone);
      }

      // Build form data for attachment (multipart/form-data)
      const bodyFormData = new FormData();
      if (formData.attachments) {
        bodyFormData.append('attachment', formData.attachments);
      }

      const response = await api.post(
        `/dashboard/order-ticket/raise?${params.toString()}`,
        bodyFormData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Backend attachment upload + notifications bhejta hai, 10s default kam padta hai
          timeout: 60000
        }
      );

      const result = response.data;

      if (result.success) {
        setSuccess(
          `Ticket raised successfully! Ticket Number: ${result.ticketNumber}`
        );

        // Notify parent to disable the button immediately
        if (onSuccess) onSuccess(formData.orderId);

        // Reset form fields (keep orderId/orderRefId)
        setFormData(prev => ({
          countryCode: defaultCountryCode,
          countryCodeId: defaultCountryId,
          alternativeMobileNumber: '',
          orderId: prev.orderId,
          orderRefId: prev.orderRefId,
          ticketCategory: '',
          subject: '',
          description: '',
          ticketPriority: '',
          attachments: null
        }));
        if (fileInputRef.current) fileInputRef.current.value = '';

        // Close dialog after 2.5 seconds
        setTimeout(() => {
          onClose();
        }, 2500);
      } else {
        setError(result.message || 'Failed to raise ticket. Please try again.');
      }
    } catch (err) {
      // Timeout: server ne request le li thi, ticket ban gaya ho sakta hai
      if (err.code === 'ECONNABORTED' && !err.response) {
        setError('Request is taking longer than expected. Your ticket may have been created. Please check View Tickets before trying again.');
        // View Tickets button enable karo taaki user check kar sake
        if (onSuccess) onSuccess(formData.orderId);
        return;
      }
      const serverMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to raise ticket. Please try again.';
      setError(serverMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    onClose();
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      background: 'linear-gradient(135deg, #ffffff 0%, #fef7f7 100%)',
      borderRadius: '16px',
      border: '2px solid grey',
      boxShadow: '0 20px 40px rgba(229, 62, 62, 0.15)',
      maxHeight: '90vh',
      overflow: 'visible'
    },
    '& .MuiDialogTitle-root': {
      background: 'linear-gradient(135deg, #c92828ff 0%, #c92828ff 100%)',
      color: 'white',
      borderRadius: '14px 14px 0 0',
      margin: '-2px -2px 0 -2px',
      padding: '20px 24px',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-10px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '60px',
        height: '4px',
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px'
      }
    },
    '& .MuiDialogContent-root': {
      padding: '30px 24px',
      background: 'rgba(255, 255, 255, 0.9)'
    },
    '& .MuiDialogActions-root': {
      padding: '20px 24px',
      background: 'rgba(254, 247, 247, 0.8)',
      borderTop: '1px solid rgba(229, 62, 62, 0.1)'
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(229, 62, 62, 0.3)',
        borderWidth: '2px'
      },
      '&:hover fieldset': {
        borderColor: 'rgba(229, 62, 62, 0.6)'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#e53e3e',
        boxShadow: '0 0 0 3px rgba(229, 62, 62, 0.1)'
      },
      '&.Mui-error fieldset': {
        borderColor: '#d32f2f'
      }
    },
    '& .MuiInputLabel-root': {
      color: '#666',
      '&.Mui-focused': {
        color: '#e53e3e'
      },
      '&.Mui-error': {
        color: '#d32f2f'
      }
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '4px'
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={dialogSx}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                🎫
              </Typography>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                color: 'white'
              }}
            >
              Raise Support Ticket
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {orderId && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontWeight: 'medium',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              📦 Order: #{orderId.toString().padStart(4, '0')}
              {orderRefId && (
                <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  (Ref: {orderRefId})
                </Typography>
              )}
            </Typography>
          </Box>
        )}
      </DialogTitle>

      {/* Form ko flex column banaya taaki DialogContent scroll ho aur buttons hamesha dikhein */}
      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}
      >
        <DialogContent dividers>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: '12px',
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fecaca',
                '& .MuiAlert-icon': {
                  color: '#dc2626'
                }
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: '12px',
                backgroundColor: '#f0fdf4',
                color: '#16a34a',
                border: '1px solid #bbf7d0'
              }}
            >
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 'medium' }}>
                Alternative Mobile Number
              </Typography>
              <Box display="flex" gap={1}>
                <Box sx={{ width: '220px', flexShrink: 0 }}>
                  <CountryCodeSelect
                    value={formData.countryCode}
                    selectedId={formData.countryCodeId}
                    onChange={handleCountryCodeChange}
                    options={COUNTRY_CODES}
                    disabled={isSubmitting}
                    error={!!fieldErrors.countryCode}
                  />
                </Box>
                <TextField
                  fullWidth
                  name="alternativeMobileNumber"
                  value={formData.alternativeMobileNumber}
                  onChange={handleChange}
                  placeholder="e.g. 9878986756"
                  sx={fieldSx}
                />
              </Box>
              {fieldErrors.countryCode && (
                <FormHelperText error sx={{ ml: '4px' }}>{fieldErrors.countryCode}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={fieldSx} error={!!fieldErrors.ticketCategory}>
                <InputLabel>Ticket Category *</InputLabel>
                <Select
                  name="ticketCategory"
                  value={formData.ticketCategory}
                  onChange={handleChange}
                  label="Ticket Category *"
                  required
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="RETURN_REQUEST">↩️ Return Request</MenuItem>
                  <MenuItem value="DELIVERY_ISSUE">🚚 Delivery Issue</MenuItem>
                  <MenuItem value="PAYMENT_ISSUE">💳 Payment Issue</MenuItem>
                  <MenuItem value="ORDER_STATUS">📋 Order Status</MenuItem>
                  <MenuItem value="PRODUCT_ISSUE">📦 Product Issue</MenuItem>
                  <MenuItem value="OTHER">❓ Other</MenuItem>
                </Select>
                {fieldErrors.ticketCategory && <FormHelperText>{fieldErrors.ticketCategory}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                error={!!fieldErrors.subject}
                helperText={fieldErrors.subject}
                placeholder="Brief description of your issue"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={fieldSx} error={!!fieldErrors.ticketPriority}>
                <InputLabel>Priority *</InputLabel>
                <Select
                  name="ticketPriority"
                  value={formData.ticketPriority}
                  onChange={handleChange}
                  label="Priority *"
                  required
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  <MenuItem value="LOW">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#10b981" />
                      Low
                    </Box>
                  </MenuItem>
                  <MenuItem value="MEDIUM">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#f59e0b" />
                      Medium
                    </Box>
                  </MenuItem>
                  <MenuItem value="HIGH">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#ef4444" />
                      High
                    </Box>
                  </MenuItem>
                  <MenuItem value="URGENT">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={8} height={8} borderRadius="50%" bgcolor="#b91c1c" />
                      Urgent
                    </Box>
                  </MenuItem>
                </Select>
                {fieldErrors.ticketPriority && <FormHelperText>{fieldErrors.ticketPriority}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  height: '56px',
                  borderRadius: '12px',
                  borderColor: 'rgba(229, 62, 62, 0.3)',
                  borderWidth: '2px',
                  color: '#e53e3e',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    borderColor: 'rgba(229, 62, 62, 0.6)',
                    backgroundColor: 'rgba(254, 247, 247, 0.9)'
                  }
                }}
              >
                📎 Upload Attachment
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                error={!!fieldErrors.description}
                helperText={fieldErrors.description}
                multiline
                rows={4}
                placeholder="Please describe your issue in detail..."
                sx={fieldSx}
              />
            </Grid>

            {formData.attachments && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(254, 247, 247, 0.8)',
                    border: '1px solid rgba(229, 62, 62, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      📄 Selected file:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#e53e3e' }}>
                      {formData.attachments.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={removeAttachment}
                    disabled={isSubmitting}
                    aria-label="Remove file"
                    title="Remove file"
                    sx={{
                      color: '#e53e3e',
                      backgroundColor: 'rgba(229, 62, 62, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(229, 62, 62, 0.2)'
                      }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography
                variant="caption"
                display="block"
                sx={{
                  color: '#666',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                📎 Max file size: 20MB (PDF, JPG, PNG only)
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ gap: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: '12px',
              padding: '12px 24px',
              color: '#666',
              border: '2px solid rgba(15, 14, 14, 0.2)',
              textTransform: 'none',
              fontWeight: 'medium',
              '&:hover': {
                backgroundColor: 'rgba(254, 247, 247, 0.5)',
                borderColor: 'rgba(10, 1, 1, 0.4)'
              }
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              borderRadius: '12px',
              padding: '12px 32px',
              background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
              boxShadow: '0 8px 20px rgba(229, 62, 62, 0.3)',
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                background: 'linear-gradient(135deg, #c53030 0%, #b91c1c 100%)',
                boxShadow: '0 12px 24px rgba(229, 62, 62, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'rgba(229, 62, 62, 0.6)',
                color: 'rgba(255, 255, 255, 0.7)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {isSubmitting ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
                Submitting...
              </Box>
            ) : (
              '🚀 Submit Ticket'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Order Card Grid Component
const OrderCardGrid = ({
  order,
  onOrderClick,
  onDownloadInvoice,
  imageUrl,
  isMobile,
  downloadingInvoices,
  orderRefData = [],
  onRaiseTicket,
  onViewTickets
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentRefId, setCurrentRefId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadSuccess, setUploadSuccess] = useState({});
  const [currentUploading, setCurrentUploading] = useState(null);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return '#4caf50';
      case 'shipped': return '#2196f3';
      case 'processing': return '#ff9800';
      case 'pending': return '#f44336';
      default: return '#757575';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return '#4caf50';
      case 'pending': return '#FF9800';
      case 'failed': return '#F44336';
      default: return '#757575';
    }
  };

  const formatOrderDate = (order) => {
    const dateString = order.dateOfOrder || order.createdAt;

    if (!dateString) return "N/A";

    try {
      const date = dateString.includes('/')
        ? new Date(dateString.split('/').reverse().join('-'))
        : new Date(dateString);

      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error("Date parsing error:", e);
    }

    return "N/A";
  };

  const getOrderRefId = (orderId) => {
    if (!Array.isArray(orderRefData)) return [];
    const match = orderRefData.find((item) => item.orderId === orderId);
    return match ? (match.orderRefId || []) : [];
  };

  const handleReceiptUpload = (orderRefId, e) => {
    e.stopPropagation();
    setCurrentRefId(orderRefId);
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentRefId) return;

    const refId = currentRefId.toString().trim();
    setCurrentUploading(refId);
    setUploading(true);
    setUploadError(null);

    try {
      if (!refId.match(/^[a-zA-Z0-9]+$/)) {
        throw new Error('Reference ID contains invalid characters');
      }

      const validTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];

      if (!validTypes.includes(file.type.toLowerCase())) {
        throw new Error('Only PDF, JPG, JPEG, and PNG files are allowed');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error('Authentication token missing');

      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/calculate-order-history/upload-receipt/${refId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Accept-Language': 'en'
          },
          body: formData
        }
      );

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      setUploadSuccess(prev => {
        const updated = { ...prev, [refId]: true };
        localStorage.setItem("uploadedReceipts", JSON.stringify(updated));
        return updated;
      });

      console.log(`Receipt uploaded successfully for ID: ${refId}`);

    } catch (error) {
      console.error('Receipt upload error:', error);
      setUploadError(error.message);
    } finally {
      setUploading(false);
      setCurrentUploading(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const savedStatus = localStorage.getItem("uploadedReceipts");
    if (savedStatus) {
      setUploadSuccess(JSON.parse(savedStatus));
    }
  }, []);

  const handleViewReceipt = async (orderRefId, e) => {
    if (e) e.stopPropagation();

    try {
      setUploadError(null);
      setUploading(true);

      if (!orderRefId || typeof orderRefId !== 'string') {
        throw new Error('Invalid reference ID');
      }
      const cleanedRefId = orderRefId.replace(/[^a-zA-Z0-9]/g, '');

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/calculate-order-history/get-image/${cleanedRefId}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type') || '';
      const blob = await response.blob();

      setReceiptImage({
        url: URL.createObjectURL(blob),
        isPDF: contentType.includes('application/pdf'),
        refId: cleanedRefId
      });
      setCurrentRefId(cleanedRefId);
      setShowReceiptModal(true);

    } catch (error) {
      console.error('Error viewing receipt:', error);
      setUploadError(error.message);
      alert(`Receipt not uploaded yet`);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    if (receiptImage?.url) {
      URL.revokeObjectURL(receiptImage.url);
    }
    setReceiptImage(null);
  };

  const orderRefIds = getOrderRefId(order.orderId);
  const primaryRefId = orderRefIds.length > 0
    ? orderRefIds[0]
    : order.orderId?.toString().padStart(4, "0");

  const totalShippingCost = (
    (order.carPrice || 0) +
    (order.baseShippingCost || 0)
  ).toFixed(2);

  return (
    <>
      <StyledCard
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            transform: "translateY(-4px)",
          },
          transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
        }}
      >
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <CardMedia
            component="img"
            height="200"
            image={imageUrl || "/carousel/carslider.png"}
            alt={`Order ${order.orderId}`}
            sx={{
              transition: "transform 0.5s ease",
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              objectFit: "cover",
              minHeight:"200px"
            }}
          />

          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              fontWeight: 700,
              lineHeight: 1.2,
              mt: 2,
              ml: 2
            }}
          >
            {order.carBrandName || "Car"} {order.carModelName || "Model"}
          </Typography>
        </Box>

        <CardContent sx={{ flexGrow: 1, p: isMobile ? 1.5 : 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: isMobile ? "1rem" : "1.125rem",
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              OrderID #{order.orderId?.toString().padStart(4, '0')}
            </Typography>

            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.65rem" }}
              >
                Reference ID:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.90rem",
                  fontWeight: "bold",
                  display: 'inline-block',
                  ml: 0.5,
                }}
              >
                #{orderRefIds.length > 0 ? orderRefIds[0] : order.orderRefId}
              </Typography>

              <Box sx={{ mt: 1 }}>
                {orderRefIds.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {orderRefIds.map((refId, index) => (
                      <Box key={refId + index} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={`${refId}`}
                          size="small"
                          sx={{ fontSize: "0.65rem", alignSelf: 'flex-start' }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleReceiptUpload(refId, e)}
                            disabled={uploadSuccess[refId] || currentUploading === refId}
                            sx={{
                              minWidth: '70px',
                              height: '24px',
                              fontSize: '0.6rem',
                              color: uploadSuccess[refId] ? '#4CAF50' : 'primary.main',
                              borderColor: uploadSuccess[refId] ? '#4CAF50' : 'primary.main',
                              '&:hover': !uploadSuccess[refId] && !currentUploading && {
                                backgroundColor: 'primary.light',
                                color: 'white'
                              },
                              '&.Mui-disabled': {
                                color: uploadSuccess[refId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                                borderColor: uploadSuccess[refId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                                opacity: 1
                              }
                            }}
                            startIcon={
                              currentUploading === refId ? (
                                <CircularProgress size={12} />
                              ) : uploadSuccess[refId] ? (
                                <CheckCircleIcon sx={{
                                  fontSize: '12px !important',
                                  color: '#4CAF50'
                                }} />
                              ) : (
                                <AttachFileIcon sx={{ fontSize: '12px !important' }} />
                              )
                            }
                          >
                            {currentUploading === refId ? 'Uploading...' : uploadSuccess[refId] ? 'Uploaded' : 'Upload'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleViewReceipt(refId, e)}
                            sx={{
                              minWidth: '60px',
                              height: '24px',
                              fontSize: '0.6rem',
                              color: 'success.main',
                              borderColor: 'success.main',
                              '&:hover': {
                                backgroundColor: 'success.light',
                                color: 'white'
                              }
                            }}
                            startIcon={<VisibilityIcon sx={{ fontSize: '12px !important' }} />}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleReceiptUpload(order.orderRefId, e)}
                      disabled={uploadSuccess[order.orderRefId] || currentUploading === order.orderRefId}
                      sx={{
                        minWidth: '70px',
                        height: '24px',
                        fontSize: '0.6rem',
                        color: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'primary.main',
                        borderColor: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'primary.main',
                        borderRadius: '10px',
                        '&:hover': !uploadSuccess[order.orderRefId] && !currentUploading && {
                          backgroundColor: 'primary.light',
                          color: 'white'
                        },
                        '&.Mui-disabled': {
                          color: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                          borderColor: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                          opacity: 1
                        }
                      }}
                      startIcon={
                        currentUploading === order.orderRefId ? (
                          <CircularProgress size={12} />
                        ) : uploadSuccess[order.orderRefId] ? (
                          <CheckCircleIcon sx={{
                            fontSize: '12px !important',
                            color: '#4CAF50'
                          }} />
                        ) : (
                          <AttachFileIcon sx={{ fontSize: '12px !important' }} />
                        )
                      }
                    >
                      {currentUploading === order.orderRefId ? 'Uploading...' : uploadSuccess[order.orderRefId] ? 'Uploaded' : 'Upload'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleViewReceipt(order.orderRefId, e)}
                      sx={{
                        minWidth: '60px',
                        height: '24px',
                        fontSize: '0.6rem',
                        color: 'success.main',
                        borderColor: 'success.main',
                        borderRadius: '10px',
                        '&:hover': {
                          backgroundColor: 'success.light',
                          color: 'white'
                        }
                      }}
                      startIcon={<VisibilityIcon sx={{ fontSize: '12px !important' }} />}
                    >
                      View
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {order.invoiceNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "0.75rem" : "0.8125rem",
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Receipt fontSize="small" />
                  Invoice: {order.invoiceNumber}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={downloadingInvoices[order.invoiceNumber]}
                  startIcon={
                    downloadingInvoices[order.invoiceNumber] ?
                      <CircularProgress size={12} /> :
                      <Download fontSize="small" />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadInvoice(order.invoiceNumber);
                  }}
                  sx={{
                    ml: 1,
                    fontSize: '0.6rem',
                    p: '2px 6px',
                    minWidth: 0,
                    borderColor: 'red',
                    color: 'red',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 0.08)',
                      borderColor: 'red',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(255, 0, 0, 0.3)',
                      color: 'rgba(255, 0, 0, 0.3)',
                    }
                  }}
                >
                  {downloadingInvoices[order.invoiceNumber] ? 'Downloading...' : 'Download'}
                </Button>
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              <CalendarToday fontSize="small" />
              {formatOrderDate(order)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.65rem" }}
              >
                Payment:
              </Typography>
              <Chip
                label={
                  orderRefIds.some((refId) => uploadSuccess[refId])
                    ? "Processing"
                    : order.paymentStatus || "Unpaid"
                }
                size="small"
                sx={{
                  height: "18px",
                  fontSize: "0.6rem",
                  backgroundColor: orderRefIds.some((refId) => uploadSuccess[refId])
                    ? "#FFA726"
                    : getPaymentStatusColor(order.paymentStatus),
                  color: "white",
                  ml: 0.5,
                }}
              />
            </Box>

            <Box sx={{ gridColumn: "span 2" }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.65rem" }}
              >
                Total Shipping Cost:
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: isMobile ? "1rem" : "1.125rem",
                  fontWeight: 700,
                  color: "error.main",
                  ml: 0.5,
                }}
              >
                ${totalShippingCost}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mt: 2,
            }}
          >
            {/* View Details button commented out — user can click card to see details */}
            {/* <Button
              variant="outlined"
              size="small"
              sx={{
                bgcolor: "transparent",
                color: "red",
                border: "1px solid red",
                borderRadius: "50px",
                fontSize: "0.75rem",
                padding: "18px 18px",
                minWidth: "150px",
                height: "38px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onOrderClick(order.orderId);
              }}
            >
              View Details
            </Button> */}
            <Button
              variant="contained"
              size="small"
              sx={{
                bgcolor: "transparent",
                color: "red",
                border: "1px solid red",
                borderRadius: "50px",
                fontSize: "0.75rem",
                padding: "18px 18px",
                minWidth: "150px",
                height: "38px",
                transition: "all 0.3s ease",
                overflow: "hidden",
                position: "relative",
                zIndex: 1,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "red",
                  transform: "translateX(-100%)",
                  transition: "transform 0.3s ease",
                  zIndex: -1,
                },
                "&:hover": {
                  color: "white",
                  boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                  "&::before": {
                    transform: "translateX(0)",
                  },
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onRaiseTicket(order.orderId, orderRefIds.length > 0 ? orderRefIds[0] : order.orderRefId);
              }}
            >
              Raise Ticket
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!order.ticketStatus}
              sx={{
                bgcolor: !order.ticketStatus ? "#f5f5f5" : "transparent",
                color: !order.ticketStatus ? "grey" : "red",
                border: !order.ticketStatus ? "1px solid #ccc" : "1px solid red",
                borderRadius: "50px",
                fontSize: "0.75rem",
                padding: "18px 18px",
                minWidth: "150px",
                height: "38px",
                transition: "all 0.3s ease",
                overflow: "hidden",
                position: "relative",
                zIndex: 1,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: !order.ticketStatus ? "grey" : "red",
                  transform: "translateX(-100%)",
                  transition: "transform 0.3s ease",
                  zIndex: -1,
                },
                "&:hover": {
                  color: !order.ticketStatus ? "grey" : "white",
                  boxShadow: !order.ticketStatus ? "none" : "0 3px 6px rgba(255,0,0,0.3)",
                  "&::before": {
                    transform: !order.ticketStatus ? "translateX(-100%)" : "translateX(0)",
                  },
                },
                "&.Mui-disabled": {
                  color: "grey",
                  border: "1px solid #ccc",
                  bgcolor: "#f5f5f5",
                  cursor: "not-allowed",
                  pointerEvents: "auto",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (order.ticketStatus) {
                  onViewTickets(order.orderId);
                }
              }}
            >
              View Tickets
            </Button>
          </Box>
        </CardContent>
      </StyledCard>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*,.pdf"
        style={{ display: 'none' }}
      />

      <Modal
        open={showReceiptModal}
        onClose={handleCloseReceiptModal}
        aria-labelledby="receipt-modal-title"
        aria-describedby="receipt-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <Typography id="receipt-modal-title" variant="h6" component="h2" gutterBottom>
            Receipt for Order #{currentRefId}
          </Typography>
          {receiptImage ? (
            receiptImage.isPDF ? (
              <Box sx={{ width: '100%', height: '70vh' }}>
                <iframe
                  src={receiptImage.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title={`Receipt PDF for order ${currentRefId}`}
                />
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = receiptImage.url;
                      link.download = `receipt-${currentRefId}.pdf`;
                      link.click();
                    }}
                    sx={{ mr: 1 }}
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(receiptImage.url, '_blank')}
                  >
                    Open in New Tab
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                component="img"
                src={receiptImage.url || receiptImage}
                alt={`Receipt for order ${currentRefId}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )
          ) : (
            <Typography>Loading receipt...</Typography>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseReceiptModal} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={!!uploadError}
        autoHideDuration={6000}
        onClose={() => setUploadError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {uploadError}
        </Alert>
      </Snackbar>
    </>
  );
};

// Order Table Component for List View
const OrderTable = ({ orders, onOrderClick, onDownloadInvoice, imageUrl, downloadingInvoices, orderRefData, onRaiseTicket, onViewTickets }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");
  const visibleColumns = isMobile ? 4 : isTablet ? 5 : 6;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentRefId, setCurrentRefId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadSuccess, setUploadSuccess] = useState({});
  const [currentUploading, setCurrentUploading] = useState(null);
  const navigate = useNavigate();

  const getOrderRefId = (orderId) => {
    if (!Array.isArray(orderRefData)) return [];
    const match = orderRefData.find((item) => item.orderId === orderId);
    return match ? (match.orderRefId || []) : [];
  };

  const handleReceiptUpload = (orderRefId, e) => {
    e.stopPropagation();
    setCurrentRefId(orderRefId);
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentRefId) return;

    const refId = currentRefId.toString().trim();
    setCurrentUploading(refId);
    setUploading(true);
    setUploadError(null);

    try {
      if (!refId.match(/^[a-zA-Z0-9]+$/)) {
        throw new Error('Reference ID contains invalid characters');
      }

      const validTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];

      if (!validTypes.includes(file.type.toLowerCase())) {
        throw new Error('Only PDF, JPG, JPEG, and PNG files are allowed');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error('Authentication token missing');

      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/calculate-order-history/upload-receipt/${refId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Accept-Language': 'en'
          },
          body: formData
        }
      );

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      setUploadSuccess(prev => {
        const updated = { ...prev, [refId]: true };
        localStorage.setItem("uploadedReceipts", JSON.stringify(updated));
        return updated;
      });

      console.log(`Receipt uploaded successfully for ID: ${refId}`);

    } catch (error) {
      console.error('Receipt upload error:', error);
      setUploadError(error.message);
    } finally {
      setUploading(false);
      setCurrentUploading(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const savedStatus = localStorage.getItem("uploadedReceipts");
    if (savedStatus) {
      setUploadSuccess(JSON.parse(savedStatus));
    }
  }, []);

  const handleViewReceipt = async (orderRefId, e) => {
    if (e) e.stopPropagation();

    try {
      setUploadError(null);
      setUploading(true);

      if (!orderRefId || typeof orderRefId !== 'string') {
        throw new Error('Invalid reference ID');
      }
      const cleanedRefId = orderRefId.replace(/[^a-zA-Z0-9]/g, '');

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/calculate-order-history/get-image/${cleanedRefId}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type') || '';
      const blob = await response.blob();

      setReceiptImage({
        url: URL.createObjectURL(blob),
        isPDF: contentType.includes('application/pdf'),
        refId: cleanedRefId
      });
      setCurrentRefId(cleanedRefId);
      setShowReceiptModal(true);

    } catch (error) {
      console.error('Error viewing receipt:', error);
      setUploadError(error.message);
      alert(`Receipt not uploaded yet`);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    if (receiptImage?.url) {
      URL.revokeObjectURL(receiptImage.url);
    }
    setReceiptImage(null);
  };

  const formatOrderDate = (order) => {
    // Get the date string from the order object
    const dateString = order?.dateOfOrder || order?.createdAt || order?.orderDate;

    if (!dateString) {
      console.warn("No date found in order object:", order);
      return "N/A";
    }

    // If already in DD/MM/YYYY format, return as is
    if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      // Ensure proper padding for single digit days/months
      const [day, month, year] = dateString.split('/');
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }

    // Handle DD-MM-YYYY format
    if (typeof dateString === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('-');
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }

    // Handle ISO format (YYYY-MM-DD) or other standard formats
    try {
      const date = new Date(dateString);

      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error("Date parsing error:", error);
    }

    // If all parsing fails, return the original string or N/A
    return typeof dateString === 'string' ? dateString : "N/A";
  };

  // Example usage with your API response
  const sampleOrder = {
    "dateOfOrder": "29/08/2025"
  };

  console.log(formatOrderDate(sampleOrder)); // Output: "29/08/2025"

  // Test with various date formats




  return (
    <>
      <StyledTableContainer component={Paper}>
        <Table stickyHeader sx={{ minWidth: isMobile ? "650px" : "800px" }}>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>Order Details</StyledTableHeaderCell>
              {visibleColumns > 1 && (
                <StyledTableHeaderCell align="center">Amount</StyledTableHeaderCell>
              )}
              {visibleColumns > 2 && (
                <StyledTableHeaderCell align="center">Reference ID</StyledTableHeaderCell>
              )}
              {visibleColumns > 3 && (
                <StyledTableHeaderCell align="center">Payment</StyledTableHeaderCell>
              )}
              {visibleColumns > 4 && (
                <StyledTableHeaderCell align="center">Order Date</StyledTableHeaderCell>
              )}
              <StyledTableHeaderCell align="center">Actions</StyledTableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {orders.map((order) => {
              const orderRefId = getOrderRefId(order.orderId);
              const primaryRefId = orderRefId.length > 0 ? orderRefId[0] : order.orderId.toString().padStart(4, "0");

              return (
                <StyledTableRow
                  key={order.orderId}
                >
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }} onClick={() => onOrderClick(order)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          position: "relative",
                          width: isMobile ? 60 : 80,
                          height: isMobile ? 45 : 60,
                        }}
                      >
                        <Box
                          component="img"
                          src={imageUrl[order.carModelId]}
                          alt={`Order ${order.carModelId}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 1,
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          #{order.orderRefID || primaryRefId}
                        </Typography>
                        {order.carBrandName || "Car"}{" "}
                        {order.carModelName || "Model"}
                      </Box>
                    </Box>
                  </TableCell>

                  {visibleColumns > 1 && (
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: isMobile ? "0.75rem" : "0.95rem",
                          color: "error.main",
                        }}
                      >
                        ${(
                          (order.carPrice || 0) +
                          (order.totalCostAllCars || 0)
                        ).toFixed(2)}
                      </Typography>
                    </TableCell>
                  )}

                  {visibleColumns > 2 && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: ".90rem", fontWeight: "bold" }}
                        >
                          #{orderRefId.length > 0 ? orderRefId[0] : order.orderRefId}
                        </Typography>

                        {orderRefId.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                            {orderRefId.map((refId, index) => (
                              <Box key={refId + index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Chip label={`${refId}`} size="small" sx={{ fontSize: "0.65rem" }} />

                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => handleReceiptUpload(refId, e)}
                                  disabled={uploadSuccess[refId] || currentUploading === refId}
                                  sx={{
                                    minWidth: '70px',
                                    height: '24px',
                                    fontSize: '0.6rem',
                                    color: uploadSuccess[refId] ? '#4CAF50' : 'primary.main',
                                    borderColor: uploadSuccess[refId] ? '#4CAF50' : 'primary.main',
                                    '&:hover': !uploadSuccess[refId] && !currentUploading && {
                                      backgroundColor: 'primary.light',
                                      color: 'white'
                                    },
                                    '&.Mui-disabled': {
                                      color: uploadSuccess[refId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                                      borderColor: uploadSuccess[refId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                                      opacity: 1
                                    }
                                  }}
                                  startIcon={
                                    currentUploading === refId ? (
                                      <CircularProgress size={12} />
                                    ) : uploadSuccess[refId] ? (
                                      <CheckCircleIcon sx={{
                                        fontSize: '12px !important',
                                        color: '#4CAF50'
                                      }} />
                                    ) : (
                                      <AttachFileIcon sx={{ fontSize: '12px !important' }} />
                                    )
                                  }
                                >
                                  {currentUploading === refId ? 'Uploading...' : uploadSuccess[refId] ? 'Uploaded' : 'Upload'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => handleViewReceipt(refId, e)}
                                  sx={{
                                    minWidth: '60px',
                                    height: '24px',
                                    fontSize: '0.6rem',
                                    color: 'success.main',
                                    borderColor: 'success.main',
                                    '&:hover': {
                                      backgroundColor: 'success.light',
                                      color: 'white'
                                    }
                                  }}
                                  startIcon={<VisibilityIcon sx={{ fontSize: '12px !important' }} />}
                                >
                                  View
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{
                            display: 'flex',
                            gap: 0.5,
                          }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => handleReceiptUpload(order.orderRefId, e)}
                              disabled={uploadSuccess[order.orderRefId] || currentUploading === order.orderRefId}
                              sx={{
                                minWidth: '70px',
                                height: '24px',
                                fontSize: '0.6rem',
                                color: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'primary.main',
                                borderColor: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'primary.main',
                                borderRadius: '10px',
                                '&:hover': !uploadSuccess[order.orderRefId] && !currentUploading && {
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                },
                                '&.Mui-disabled': {
                                  color: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                                  borderColor: uploadSuccess[order.orderRefId] ? '#4CAF50' : 'rgba(0, 0, 0, 0.26)',
                                  opacity: 1
                                }
                              }}
                              startIcon={
                                currentUploading === order.orderRefId ? (
                                  <CircularProgress size={12} />
                                ) : uploadSuccess[order.orderRefId] ? (
                                  <CheckCircleIcon sx={{
                                    fontSize: '12px !important',
                                    color: '#4CAF50'
                                  }} />
                                ) : (
                                  <AttachFileIcon sx={{ fontSize: '12px !important' }} />
                                )
                              }
                            >
                              {currentUploading === order.orderRefId ? 'Uploading...' : uploadSuccess[order.orderRefId] ? 'Uploaded' : 'Upload'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => handleViewReceipt(order.orderRefId, e)}
                              sx={{
                                minWidth: '60px',
                                height: '24px',
                                fontSize: '0.6rem',
                                color: 'success.main',
                                borderColor: 'success.main',
                                borderRadius: '10px',
                                '&:hover': {
                                  backgroundColor: 'success.light',
                                  color: 'white'
                                }
                              }}
                              startIcon={<VisibilityIcon sx={{ fontSize: '12px !important' }} />}
                            >
                              View
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  )}

                  {visibleColumns > 3 && (
                    <TableCell align="center">
                      <Chip
                        label={
                          orderRefId.some((refId) => uploadSuccess[refId])
                            ? "Processing"
                            : order.paymentStatus || "Unpaid"
                        }
                        size="small"
                        sx={{
                          backgroundColor: orderRefId.some((refId) => uploadSuccess[refId])
                            ? "#FFA726"
                            : (order.paymentStatus === "Unpaid" ? "#f44336" : "gray"),
                          color: "white",
                          fontSize: "0.65rem",
                          height: "20px",
                        }}
                      />
                    </TableCell>
                  )}

                  {visibleColumns > 4 && (
                    <TableCell align="center">
                      <Typography sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}>
                        {formatOrderDate(order)}
                      </Typography>
                    </TableCell>
                  )}

                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {/* Details button commented out — user can click row to see details */}
                      {/* <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          bgcolor: "transparent",
                          color: "red",
                          border: "1px solid red",
                          borderRadius: "50px",
                          fontSize: "0.75rem",
                          padding: "18px 18px",
                          minWidth: "150px",
                          height: "38px",
                        }}
                        onClick={() => onOrderClick(order)}
                      >
                        Details
                      </Button> */}
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          bgcolor: "transparent",
                          color: "red",
                          border: "1px solid red",
                          borderRadius: "50px",
                          fontSize: "0.75rem",
                          padding: "18px 18px",
                          minWidth: "150px",
                          height: "38px",
                          transition: "all 0.3s ease",
                          overflow: "hidden",
                          position: "relative",
                          zIndex: 1,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "red",
                            transform: "translateX(-100%)",
                            transition: "transform 0.3s ease",
                            zIndex: -1,
                          },
                          "&:hover": {
                            color: "white",
                            boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                            "&::before": {
                              transform: "translateX(0)",
                            },
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRaiseTicket(order.orderId, orderRefId.length > 0 ? orderRefId[0] : order.orderRefId);
                        }}
                      >
                        Raise Ticket
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!order.ticketStatus}
                        sx={{
                          bgcolor: !order.ticketStatus ? "#f5f5f5" : "transparent",
                          color: !order.ticketStatus ? "grey" : "red",
                          border: !order.ticketStatus ? "1px solid #ccc" : "1px solid red",
                          borderRadius: "50px",
                          fontSize: "0.75rem",
                          padding: "18px 18px",
                          minWidth: "150px",
                          height: "38px",
                          transition: "all 0.3s ease",
                          overflow: "hidden",
                          position: "relative",
                          zIndex: 1,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: !order.ticketStatus ? "grey" : "red",
                            transform: "translateX(-100%)",
                            transition: "transform 0.3s ease",
                            zIndex: -1,
                          },
                          "&:hover": {
                            color: !order.ticketStatus ? "grey" : "white",
                            boxShadow: !order.ticketStatus ? "none" : "0 3px 6px rgba(255,0,0,0.3)",
                            "&::before": {
                              transform: !order.ticketStatus ? "translateX(-100%)" : "translateX(0)",
                            },
                          },
                          "&.Mui-disabled": {
                            color: "grey",
                            border: "1px solid #ccc",
                            bgcolor: "#f5f5f5",
                            cursor: "not-allowed",
                            pointerEvents: "auto",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (order.ticketStatus) {
                            onViewTickets(order.orderId);
                          }
                        }}
                      >
                        View Tickets
                      </Button>
                      {/* Product Review Button with same red styling */}
                     <Button
  variant="contained"
  size="small"
  sx={{
    bgcolor: "transparent",
    color: "red",
    border: "1px solid red",
    borderRadius: "50px",
    fontSize: "0.75rem",
    padding: "18px 18px",
    minWidth: "150px",
    height: "38px",
    transition: "all 0.3s ease",
    overflow: "hidden",
    position: "relative",
    zIndex: 1,
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "red",
      transform: "translateX(-100%)",
      transition: "transform 0.3s ease",
      zIndex: -1,
    },
    "&:hover": {
      color: "white",
      boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
      "&::before": {
        transform: "translateX(0)",
      },
    },
  }}
  onClick={() => {
    // First save the necessary data to localStorage
    localStorage.setItem('currentOrderId', order.orderId);
    
    // Try to get carModelId from order data
    // Adjust based on your actual data structure
    const carModelId = order.carId || order.carModelId || order.productId || order.itemId;
    if (carModelId) {
      localStorage.setItem('currentCarModelId', carModelId);
    }
    
    // Also save the entire order object for reference
    localStorage.setItem('currentOrderData', JSON.stringify(order));
    
    // Navigate to the review page
    navigate('/productReview', {
      state: {
        orderId: order.orderId,
        carModelId: carModelId,
        orderData: order
      }
    });
  }}
>
  Product Review
</Button>


                    </Box>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*,.pdf"
        style={{ display: 'none' }}
      />

      <Modal
        open={showReceiptModal}
        onClose={handleCloseReceiptModal}
        aria-labelledby="receipt-modal-title"
        aria-describedby="receipt-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <Typography id="receipt-modal-title" variant="h6" component="h2" gutterBottom>
            Receipt for Order #{currentRefId}
          </Typography>
          {receiptImage ? (
            receiptImage.isPDF ? (
              <Box sx={{ width: '100%', height: '70vh' }}>
                <iframe
                  src={receiptImage.url}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title={`Receipt PDF for order ${currentRefId}`}
                />
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = receiptImage.url;
                      link.download = `receipt-${currentRefId}.pdf`;
                      link.click();
                    }}
                    sx={{ mr: 1 }}
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(receiptImage.url, '_blank')}
                  >
                    Open in New Tab
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                component="img"
                src={receiptImage.url || receiptImage}
                alt={`Receipt for order ${currentRefId}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )
          ) : (
            <Typography>Loading receipt...</Typography>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseReceiptModal} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={!!uploadError}
        autoHideDuration={6000}
        onClose={() => setUploadError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {uploadError}
        </Alert>
      </Snackbar>
    </>
  );
};

// Main OrderHistory Component
const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [imageUrl, setImageUrl] = useState({});
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [showRaiseTicket, setShowRaiseTicket] = useState(false);
  const [selectedOrderForTicket, setSelectedOrderForTicket] = useState({ id: '', refId: '' });

  const isMobile = useMediaQuery("(max-width:600px)");
  useEffect(() => {
  if (isMobile) {
    setViewMode("grid");   // 📱 Mobile → Grid
  } else {
    setViewMode("list");   // 💻 Desktop → List
  }
}, [isMobile]);
  const navigate = useNavigate();

  const fetchCarImage = async (carModelId) => {
    try {
      if (!carModelId) return null;

      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`
      );

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error("Error fetching car image:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchImages = async () => {
      if (orders && orders.length > 0) {
        const newImageUrls = {};

        for (const order of orders) {
          if (order.carModelId && !imageUrl[order.carModelId]) {
            const fetchedImageUrl = await fetchCarImage(order.carModelId);
            if (fetchedImageUrl) {
              newImageUrls[order.carModelId] = fetchedImageUrl;
            }
          }
        }

        if (Object.keys(newImageUrls).length > 0) {
          setImageUrl(prev => ({ ...prev, ...newImageUrls }));
        }
      }
    };

    fetchImages();
  }, [orders]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(
        "https://car.amar.co.jp/api/authorization/calculate-order-history/get",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        }
      );

      console.log("Order API response:", response.data);

      if (!response.data.success || !Array.isArray(response.data.data)) {
        throw new Error("Unexpected API response format");
      }

      const mappedData = response.data.data.map((item) => ({
        orderId: item.id,
        orderRefId: item.orderRefId,
        status: item.orderPlaced ? "Completed" : "Pending",
        createdAt: item.createdAt || new Date().toISOString(),
        carModelId: item.carModelId,
        carBrandName: item.carBrandName || "N/A",
        carModelName: item.carModelName || "N/A",
        carCategoriesName: item.carCategoriesName || "N/A",
        carPrice: item.carPrice || 0,
        carPriceCurrency: item.carPriceCurrency || "USD",
        carRefNumber: item.carRefNumber || "N/A",
        carRegistrationDate: item.carRegistrationDate || "N/A",
        carManufactureDate: item.carManufactureDate || "N/A",
        carModelCode: item.carModelCode || "N/A",
        carGrade: item.carGrade || "N/A",
        carChassisNumber: item.carChassisNumber || "N/A",
        carEngineCC: item.carEngineCC || "N/A",
        carEngineCode: item.carEngineCode || "N/A",
        carFuel: item.carFuel || "N/A",
        carTransmission: item.carTransmission || "N/A",
        carSteering: item.carSteering || "N/A",
        carSeat: item.carSeat || "N/A",
        carDoor: item.carDoor || "N/A",
        carMileage: item.carMileage || "N/A",
        carColor: item.carColor || "N/A",
        carSize: item.carSize || "N/A",
        carM3: item.carM3 || "N/A",
        carWeight: item.carWeight || "N/A",
        carDrive: item.carDrive || "N/A",
        carLocation: item.carLocation || "N/A",
        carLength: item.carLength || "N/A",
        carWidth: item.carWidth || "N/A",
        carHeight: item.carHeight || "N/A",
        ourPick: item.ourPick || false,
        specialDeal: item.specialDeal || false,
        airConditioning: item.airConditioning || "N/A",
        shippingOption: item.shippingOption || "N/A",
        portName: item.portName || "N/A",
        countryName: item.countryName || "N/A",
        volumeCubicMeters: item.volumeCubicMeters || 0,
        totalCostAllCars: item.totalCostAllCars || 0,
        totalCost: item.totalCost || 0,
        dateOfOrder: item.dateOfOrder || item.createdAt,
        ticketStatus: item.ticketStatus || null,
      }));

      setOrders(mappedData);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError(error.message || "Failed to load order history");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDownloadInvoice = async (orderRefId) => {
    try {
      setDownloadingInvoice(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(
        `https://car.amar.co.jp/api/authorization/user-order-invoice/download/${orderRefId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${orderRefId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      setError("Failed to download invoice. Please try again.");
      setShowError(true);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) setViewMode(newViewMode);
  };

  const handleOrderClick = (order) => {
    const normalizedOrder = {
      ...order,
      orderId: order.orderId,
      orderRefId: order.orderRefId,
      status: order.status,
      createdAt: order.createdAt,
      dateOfOrder: order.dateOfOrder,
      carDetails: {
        model: order.carModelName || "N/A",
        year: order.carManufactureDate || "N/A",
        vin: order.carChassisNumber || "N/A",
        carRefNumber: order.carRefNumber || "N/A",
        carRegistrationDate: order.carRegistrationDate || "N/A",
        carManufactureDate: order.carManufactureDate || "N/A",
        carModelCode: order.carModelCode || "N/A",
        carGrade: order.carGrade || "N/A",
        carChassisNumber: order.carChassisNumber || "N/A",
        carEngineCC: order.carEngineCC || "N/A",
        carEngineCode: order.carEngineCode || "N/A",
        carFuel: order.carFuel || "N/A",
        carTransmission: order.carTransmission || "N/A",
        carSteering: order.carSteering || "N/A",
        carSeat: order.carSeat || "N/A",
        carDoor: order.carDoor || "N/A",
        carMileage: order.carMileage || "N/A",
        carColor: order.carColor || "N/A",
        carSize: order.carSize || "N/A",
        carM3: order.carM3 || "N/A",
        carWeight: order.carWeight || "N/A",
        carDrive: order.carDrive || "N/A",
        carLocation: order.carLocation || "N/A",
        carLength: order.carLength || "N/A",
        carWidth: order.carWidth || "N/A",
        carHeight: order.carHeight || "N/A",
        ourPick: order.ourPick ? "Yes" : "No",
        specialDeal: order.specialDeal ? "Yes" : "No",
        airConditioning: order.airConditioning || "N/A",
        carModelName: order.carModelName || "N/A",
        carBrandName: order.carBrandName || "N/A",
        carCategoriesName: order.carCategoriesName || "N/A",
        carPrice: order.carPrice || "N/A",
        carPriceCurrency: order.carPriceCurrency || "USD"
      },
      destination: {
        portName: order.portName,
        countryName: order.countryName,
      },
      shippingOption: order.shippingOption,
      volumeCubicMeters: order.volumeCubicMeters,
      carPrice: order.carPrice,
      baseShippingCost: order.baseShippingCost,
      totalCost: order.totalCost,
    };

    setSelectedOrder(normalizedOrder);
    setShowOrderDetails(true);
  };

  const handleRaiseTicket = (orderId, orderRefId) => {
    // Get car name from orders list for default ticket subject
    const order = orders.find(o => o.orderId === orderId);
    const carName = order
      ? `${(order.carBrandName || '').replace(/_/g, ' ')} ${order.carModelName || ''}`.trim()
      : '';
    setSelectedOrderForTicket({
      id: orderId,
      refId: orderRefId,
      carName
    });
    setShowRaiseTicket(true);
  };

  const handleCloseRaiseTicket = () => {
    setShowRaiseTicket(false);
    setSelectedOrderForTicket({ id: '', refId: '', carName: '' });
  };

  const handleTicketSuccess = (orderId) => {
    setOrders(prev =>
      prev.map(order =>
        order.orderId === orderId
          ? { ...order, ticketStatus: "OPEN" }
          : order
      )
    );
  };

  const handleViewTickets = (orderId) => {
    navigate(`/myAccount/orderTicketHistory/${orderId}`);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleClick = () => {
    navigate("/myAccount");
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleCloseOrderDialog = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
  };

  const totalPages = Math.ceil(orders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = orders.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ marginBottom: "0%" }}><Notes /></Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

      {/* <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "5%",
            margin: 0,
            color: "white",
            fontSize: "15px",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: "5px 10px",
            borderRadius: "5px",
          }}
        >
          <Typography
            component="span"
            sx={{
              opacity: 0.5,
              marginRight: "5px",
            }}
          >
            <Link to="http://3.110.209.177" style={{ fontSize: "13px", color: "white", textDecoration: "none" }}>
              Home/
            </Link>
          </Typography>
          <Link to="https://car.amar.co.jp/MyAccount" style={{ fontSize: "13px", color: "white", textDecoration: "none" }}>
            My Account
          </Link>
          / OrderHistory
        </Box>

        <img
          src="images/wishlist.png"
          alt="Order History Background"
          style={{ width: "100%", height: "50vh", objectFit: "cover" }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textShadow: "2px 2px 8px rgba(0, 0, 0, 0.7)",
            fontSize: { xs: "20px", sm: "24px", md: "28px", lg: "30px" },
            fontFamily: "Roboto",
            textAlign: "center",
            width: "100%",
            color: "#EBEBEB",
          }}
        >
          <b>Order / History</b>
          <br />
          <button
            onClick={handleClick}
            style={{
              backgroundColor: "#EB2329",
              color: "white",
              border: "none",
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              cursor: "pointer",
              textTransform: "uppercase",
              transition: "background-color 0.3s ease",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c41f23")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#EB2329")}
          >
            My Account
          </button>
        </Box>
      </Box> */}
      <Box
  className="image-container-OrderHistory"
  sx={{
    position: "relative",
    width: "100%",
    height: { xs: "30vh", sm: "40vh", md: "50vh" },
    backgroundColor: "black",
  }}
>
  <Box
    component="img"
    src="/images/wishlist.png"
    alt="Order History Background"
    sx={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
      opacity: 0.3,
    }}
  />

  <Box
    sx={{
      position: "absolute",
      top: { xs: "5%", sm: "5%", md: "5%" },
      left: { xs: "3%", sm: "5%", md: "5%" },
      margin: 0,
      color: "white",
      fontSize: { xs: "11px", sm: "13px", md: "15px" },
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      padding: { xs: "4px 8px", sm: "5px 10px", md: "5px 10px" },
      borderRadius: "5px",
    }}
  >
    <Typography
      component="span"
      sx={{
        opacity: 0.5,
        marginRight: { xs: "2px", sm: "5px", md: "5px" },
      }}
    >
      <Link
        to="http://3.110.209.177"
        style={{
          fontSize: "inherit",
          color: "white",
          textDecoration: "none",
        }}
      >
        Home/
      </Link>
    </Typography>

    <Link
      to="https://car.amar.co.jp/MyAccount"
      style={{
        fontSize: "inherit",
        color: "white",
        textDecoration: "none",
      }}
    >
      My Account
    </Link>

    / OrderHistory
  </Box>

  <Box
    className="support-paragraph-OrderHistory"
    sx={{
      position: "absolute",
      margin: "auto",
      color: "white",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: { xs: "1.5rem", sm: "2rem", md: "30px" },
      fontFamily: "Roboto",
      fontWeight: "700",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: { xs: "10px", sm: "15px", md: "20px" },
      textAlign: "center",
      width: "90%",
    }}
  >
    Order / History

    <button
      onClick={handleClick}
      style={{
        backgroundColor: "#EB2329",
        color: "white",
        border: "none",
        padding:
          window.innerWidth < 600
            ? "8px 16px"
            : window.innerWidth < 960
            ? "9px 18px"
            : "10px 20px",
        fontSize:
          window.innerWidth < 600
            ? "14px"
            : window.innerWidth < 960
            ? "15px"
            : "16px",
        borderRadius: "8px",
        cursor: "pointer",
        textTransform: "uppercase",
        transition: "background-color 0.3s ease",
      }}
      onMouseOver={(e) => (e.target.style.backgroundColor = "#c41f23")}
      onMouseOut={(e) => (e.target.style.backgroundColor = "#EB2329")}
    >
      My Account
    </button>
  </Box>
</Box>


      <Container
        maxWidth="xl"
        sx={{
          width: "90%",
          backgroundColor: "#FFFFFF",
          border: "2px solid #EBEBEB",
          borderRadius: "10px",
          marginTop: "-75px",
          marginBottom: "100px",
          position: "relative",
          zIndex: 1,
          minHeight: "400px",
          p: { xs: 2, md: 4 },
        }}
      >
        {error && orders.length === 0 ? (
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Alert severity="error" sx={{ mb: 3, width: "100%" }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchOrders}
              sx={{ alignSelf: "center" }}
            >
              Retry
            </Button>
          </Box>
        ) : orders.length === 0 ? (
          <Box sx={{ flex: 1, p: 3, pt: 10, textAlign: "center" }}>
            <Typography variant="h6" mb={2} color="error">
              Welcome to your Order Page!
            </Typography>
            <Typography mb={1}>
              You currently do not have any orders with us. We are here to
              assist you with your orders!
            </Typography>
            <Typography mb={3}>
              Once you place an order, your orders will be displayed here.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              href="/"
              sx={{ mt: 2, backgroundColor: "red" }}
            >
              Browse Cars
            </Button>
          </Box>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 2 : 3,
                mb: 4,
                borderRadius: "10px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: isMobile ? 2 : 0,
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      fontSize: isMobile ? "1.25rem" : "1.5rem",
                      color: "error.main",
                    }}
                  >
                    Order History
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {orders.length} {orders.length === 1 ? "order" : "orders"} found
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewMode}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiToggleButton-root": {
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      color: "black",
                      "&.Mui-selected": {
                        backgroundColor: "red",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#cc0000",
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="list">
                    <ListIcon fontSize={isMobile ? "small" : "medium"} />
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <GridView fontSize={isMobile ? "small" : "medium"} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Paper>

            {/* {viewMode === "list" ? (
              <OrderTable
                orders={paginatedOrders}
                onOrderClick={handleOrderClick}
                onDownloadInvoice={handleDownloadInvoice}
                imageUrl={imageUrl}
                downloadingInvoices={downloadingInvoice}
                onRaiseTicket={handleRaiseTicket}
              />
            ) : (
              <Grid container spacing={isMobile ? 2 : 3}>
                {paginatedOrders.map((order) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={order.orderId}>
                    <OrderCardGrid
                      order={order}
                      onOrderClick={() => handleOrderClick(order)}
                      onDownloadInvoice={handleDownloadInvoice}
                      imageUrl={
                        imageUrl[order.carModelId] || "/carousel/carslider.png"
                      }
                      isMobile={isMobile}
                      downloadingInvoices={downloadingInvoice}
                      onRaiseTicket={handleRaiseTicket}
                    />
                  </Grid>
                ))}
              </Grid>
            )} */}
            {viewMode === "grid" ? (
              <Grid container spacing={isMobile ? 2 : 3}>
                {paginatedOrders.map((order) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={order.orderId}>
                    <OrderCardGrid
                      order={order}
                      onOrderClick={() => handleOrderClick(order)}
                      onDownloadInvoice={handleDownloadInvoice}
                      imageUrl={
                        imageUrl[order.carModelId] || "/carousel/carslider.png"
                      }
                      isMobile={isMobile}
                      downloadingInvoices={downloadingInvoice}
                      onRaiseTicket={handleRaiseTicket}
                      onViewTickets={handleViewTickets}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <OrderTable
                orders={paginatedOrders}
                onOrderClick={handleOrderClick}
                onDownloadInvoice={handleDownloadInvoice}
                imageUrl={imageUrl}
                downloadingInvoices={downloadingInvoice}
                onRaiseTicket={handleRaiseTicket}
                onViewTickets={handleViewTickets}
              />
            )}

            {orders.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 4,
                  flexDirection: isMobile ? "column" : "row",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "black",
                      fontWeight: 500,
                      letterSpacing: "0.5px",
                    }}
                  >
                    Results per page:
                  </Typography>
                </Box>

                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  variant="outlined"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontWeight: 500,
                      borderColor: "black",
                      color: "black",
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.1)",
                        borderColor: "red",
                      },
                    },
                    "& .Mui-selected": {
                      backgroundColor: "red !important",
                      color: "white",
                      borderColor: "red",
                      "&:hover": {
                        backgroundColor: "#b30000 !important",
                        borderColor: "#800000",
                      },
                    },
                  }}
                />
                <Select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  variant="outlined"
                  size="small"
                  sx={{
                    width: 80,
                    color: "black",
                    fontWeight: 500,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      border: "1px solid black !important",
                    },
                    "&:hover fieldset": {
                      borderColor: "red !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "red !important",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "red",
                    },
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Box>
            )}

            <Dialog
              open={showOrderDetails}
              onClose={handleCloseOrderDialog}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">
                    Reference ID #{selectedOrder?.orderRefId || "N/A"}
                  </Typography>
                  <Box>
                    {selectedOrder && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleDownloadInvoice(selectedOrder.orderRefId)}
                        disabled={downloadingInvoice}
                        sx={{ mr: 2, backgroundColor: "red" }}
                      >
                        {downloadingInvoice ? "Downloading..." : "Download Invoice"}
                      </Button>
                    )}
                    <Button onClick={handleCloseOrderDialog} sx={{ minWidth: "auto", p: 1, color: "red" }}>
                      ×
                    </Button>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent>
                {selectedOrder ? (
                  <>
                    <Card sx={{ mb: 5, overflow: "hidden", borderRadius: "10px" }}>
                      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
                        <Link
                          to={`/product/${selectedOrder.carModelId}`}
                        >
                          <CardMedia
                            component="img"
                            image={imageUrl[selectedOrder.carModelId] || "/carousel/carslider.png"}
                            alt="Vehicle"
                            sx={{
                              width: { xs: "100%", md: "300px" },
                              height: { xs: 200, md: 250 },
                              objectFit: "cover",
                              borderRadius: 1,
                              marginTop: 5.5
                            }}
                          />
                        </Link>
                        <CardContent sx={{ flex: 5 }}>
                          <TableContainer component={Paper} variant="outlined" sx={{ mt: 3.8 }}>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell colSpan={4} sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 20 }}>
                                    {selectedOrder.carBrandName?.replace(/_/g, " ") || "N/A"}{" "}
                                    {selectedOrder.carModelName || "N/A"}
                                  </TableCell>
                                </TableRow>


                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Brand</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carBrandName || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Model</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carModelName || "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Category</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carCategoriesName || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Reference Number</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carRefNumber || "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Manufacture Date</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carManufactureDate || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Registration Date</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carRegistrationDate || "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Chassis Number</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carChassisNumber || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Engine CC</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carEngineCC ? `${selectedOrder.carDetails.carEngineCC} cc` : "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Fuel Type</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carFuel || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Transmission</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carTransmission || "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Steering</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carSteering || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Mileage</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carMileage ? `${selectedOrder.carDetails.carMileage} km` : "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Color</TableCell>
                                  <TableCell>{selectedOrder.carDetails.carColor || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Air Conditioning</TableCell>
                                  <TableCell>{selectedOrder.carDetails.airConditioning || "N/A"}</TableCell>
                                </TableRow>

                                <TableRow>
                                  <TableCell sx={{ color: "grey" }}>Our Pick</TableCell>
                                  <TableCell>{selectedOrder.carDetails.ourPick || "N/A"}</TableCell>
                                  <TableCell sx={{ color: "grey" }}>Special Deal</TableCell>
                                  <TableCell>{selectedOrder.carDetails.specialDeal || "N/A"}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>

                          <Box sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                          }}>
                            <Box textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                Shipping Option
                              </Typography>
                              <Typography variant="h6">
                                {selectedOrder.shippingOption || "N/A"}
                              </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                Volume
                              </Typography>
                              <Typography variant="h6">
                                {selectedOrder.volumeCubicMeters?.toFixed(2) || "N/A"} m³
                              </Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                Total Cost
                              </Typography>
                              <Typography variant="h6" color="error">
                                ${selectedOrder.totalCost?.toFixed(2) || "0.00"}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Box>
                    </Card>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                            Charges Breakdown
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Car Base Price</TableCell>
                                  <TableCell align="right">
                                    ${selectedOrder.carPrice?.toFixed(2) || "0.00"}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell> Shipping Cost</TableCell>
                                  <TableCell align="right">
                                    ${selectedOrder.totalCostAllCars?.toFixed(2) || "0.00"}
                                  </TableCell>
                                </TableRow>
                                <TableRow sx={{ "& td": { fontWeight: "bold" } }}>
                                  <TableCell>Total Cost</TableCell>
                                  <TableCell align="right">
                                    ${selectedOrder.totalCost?.toFixed(2) || "0.00"}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                            Shipping Information
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Shipping Option</TableCell>
                                  <TableCell>{selectedOrder.shippingOption || "N/A"}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Destination Port</TableCell>
                                  <TableCell>{selectedOrder.destination?.portName || "N/A"}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Destination Country</TableCell>
                                  <TableCell>{selectedOrder.destination?.countryName || "N/A"}</TableCell>
                                </TableRow>

                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <Typography>Loading order details...</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseOrderDialog}
                  sx={{ color: "red" }}>Close</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Container>

      <FooterManual />

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <RaiseTicketDialog
        open={showRaiseTicket}
        onClose={handleCloseRaiseTicket}
        orderId={selectedOrderForTicket.id}
        orderRefId={selectedOrderForTicket.refId}
        carName={selectedOrderForTicket.carName}
        onSuccess={handleTicketSuccess}
      />
    </Box>
  );
};

export default OrderHistory;